import React, { useState, useEffect, useRef } from 'react';
import { SearchBar } from './index';
import closeIcon from '../../assets/closeIcon.png';
import { ProductCard } from '../ProductCard/ProductCard';
import { Box } from '@mui/material';
import ScrollableProductCarousel from './ScrollableProductCarousel';

const SearchSection = ({
  medicaments = [],
  isMobile = false,
  onProductSelect,
  onOpenFilterModal,
  filterModalOpen,
  onApplyFilters,
  filteredMedicaments,
  activeFilters = { priceRange: [0, 1000], category: '' }
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [forceShowDropdown, setForceShowDropdown] = useState(false);
  const [selectedFromDropdown, setSelectedFromDropdown] = useState(false);
  const searchContainerRef = useRef(null);

  const isFilterApplied = () => {
    const defaultRange = [0, 1000];
    const isPriceFiltered =
      activeFilters.priceRange[0] > defaultRange[0] ||
      activeFilters.priceRange[1] < defaultRange[1];
    const isCategoryFiltered = activeFilters.category !== '';
    return isPriceFiltered || isCategoryFiltered;
  };

  const filteredSuggestions = searchTerm.trim()
    ? medicaments
        .filter(
          (product) =>
            product.name &&
            product.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .slice(0, 6)
    : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        if (!selectedFromDropdown) {
          setShowDropdown(false);
          setForceShowDropdown(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedFromDropdown]);

  useEffect(() => {
    if (searchTerm.trim()) handleSearch();
  }, [filteredMedicaments]);

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    const results = filteredMedicaments.filter(
      (product) =>
        product.name &&
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setSearchResults(results);
    setShowSearchResults(true);
    if (!selectedFromDropdown) {
      setShowDropdown(false);
      setForceShowDropdown(false);
    }
  };

  const handleDropdownProductSelect = (product) => {
    if (onProductSelect) {
      setSelectedFromDropdown(true);
      setShowDropdown(true);
      setForceShowDropdown(true);
      onProductSelect(product);
    }
  };

  useEffect(() => {
    if (selectedFromDropdown && !filterModalOpen) {
      setShowDropdown(true);
      setForceShowDropdown(true);
    }
  }, [filterModalOpen, selectedFromDropdown]);

  useEffect(() => {
    setSelectedFromDropdown(false);
  }, [searchTerm]);

  return (
    <div className="search-section" style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%',
          gap: isMobile ? 8 : 20,
          padding: isMobile ? '0 10px' : '0',
        }}
      >
        {/* Search input */}
        <div
          ref={searchContainerRef}
          style={{
            position: 'relative',
            flexGrow: 1,
            borderRadius: '40px',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#fff',
            border: '1px solid rgba(2,7,66,0.3)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            transition: 'all 0.3s ease',
          }}
        >
          <input
            type="text"
            placeholder={isMobile ? "Search products..." : "Search your care product..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
                setShowDropdown(true);
              }
            }}
            style={{
              flex: 1,
              padding: isMobile ? '12px 44px 12px 16px' : '14px 50px 14px 26px',
              fontSize: isMobile ? '14px' : '16px',
              border: 'none',
              outline: 'none',
              borderRadius: '40px',
              color: '#010642',
              fontFamily: 'Lato, sans-serif',
            }}
          />

          <img
            src="/external/search1537-bwbl.svg"
            alt="Search"
            onClick={() => {
              handleSearch();
              setShowDropdown(false);
              setForceShowDropdown(false);
              setSelectedFromDropdown(false);
            }}
            style={{
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: isMobile ? '22px' : '26px',
              height: isMobile ? '22px' : '26px',
              cursor: 'pointer',
              opacity: 0.7,
              transition: 'opacity 0.2s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
          />

          {(forceShowDropdown ||
            (searchTerm && medicaments.length > 0 && showDropdown)) && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                backgroundColor: '#fff',
                borderRadius: '0 0 12px 12px',
                boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                overflow: 'hidden',
              }}
            >
              <SearchBar
                medicaments={medicaments}
                setSelectedProduct={handleDropdownProductSelect}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                forceShowDropdown={forceShowDropdown}
              />
            </div>
          )}
        </div>

        {/* Filter button */}
        <Box
          onClick={onOpenFilterModal}
          sx={{
            border: isFilterApplied() ? '2px solid #131B5C' : '1px solid #ccc',
            backgroundColor: isFilterApplied() ? '#F0F4FF' : '#fff',
            p: isMobile ? '4px' : '5px',
            height: isMobile ? 40 : 44,
            width: isMobile ? 40 : 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            flexShrink: 0,
            cursor: 'pointer',
            transition: 'all 0.25s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              transform: 'scale(1.05)',
            },
          }}
        >
          <Box
            component="img"
            src="/external/vector1536-vxsw.svg"
            alt="Filter"
            sx={{
              width: isMobile ? 20 : 24,
              height: isMobile ? 20 : 24,
              filter: isFilterApplied()
                ? 'invert(21%) sepia(85%) saturate(1300%) hue-rotate(210deg) brightness(95%) contrast(90%)'
                : 'none',
              transition: 'filter 0.25s ease',
            }}
          />
        </Box>
      </div>

      {/* Search Results */}
      {showSearchResults && (
        <div style={{ width: '100%', padding: isMobile ? '0 10px' : '0' }}>
          {searchResults.length > 0 ? (
            <>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: isMobile ? 18 : 22,
                  color: '#010642',
                  marginTop: isMobile ? '20px' : '28px',
                  paddingBottom: '12px',
                  borderBottom: '1px solid #E0E0E0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                Search Results
                <button
                  className="close-button"
                  onClick={() => {
                    setSearchResults([]);
                    setShowSearchResults(false);
                    setSearchTerm('');
                  }}
                  aria-label="Close search results"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: isMobile ? '4px' : '8px',
                  }}
                >
                  <img
                    src={closeIcon}
                    alt="Close"
                    style={{ width: isMobile ? '20px' : '24px', height: isMobile ? '20px' : '24px' }}
                  />
                </button>
              </div>
              <ScrollableProductCarousel
                searchResults={searchResults}
                onProductSelect={onProductSelect}
                isMobile={isMobile}
              />
            </>
          ) : (
            <div
              style={{
                margin: '36px 0',
                color: '#777',
                fontSize: 17,
                textAlign: 'center',
                fontWeight: 500,
                minHeight: 60,
              }}
            >
              {searchTerm.trim() ? (
                <>
                  No products found for <strong>"{searchTerm}"</strong>
                </>
              ) : (
                'Please enter a product name to search.'
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchSection;
